import { createTheme, Theme } from '@mui/material/styles';
import type { WheelEvent } from 'react';
import { BrandTokens, deriveBrandTokens } from './brandTokens';
import { brandColors, coarsePointerOnly, hoverOnly } from './mixins';

// Softer, layered elevations (closer to the site's shadow-md/lg/xl feel)
const softShadow = '0 1px 3px rgba(16,24,40,0.04), 0 6px 20px rgba(16,24,40,0.06)';
const softShadowHover = '0 16px 32px rgba(16,24,40,0.10)';

/**
 * The theme for one clinic.
 *
 * Takes literal hex from {@link deriveBrandTokens} rather than the `var(--ch-*)` strings
 * the `*.styles.ts` files use, and that distinction is load-bearing: MUI parses these
 * values -- `alpha()`, `getContrastText()`, the palette augmentation -- so a custom
 * property here produces `NaN` shades and unreadable text rather than a themed component.
 * The variables are for CSS; this is for MUI. Both come from the same two colours.
 *
 * Called with nothing, it is Carefull: what a signed-out visitor sees, and what a clinic
 * that has chosen no colours gets.
 *
 * What deliberately does *not* follow the brand:
 *
 *   * `success` / `error` / `warning`. Red has to stay red. `warning` used to alias the
 *     brand gold, which was fine when the gold was fixed and is not now -- a clinic with a
 *     red secondary would have had warnings indistinguishable from errors. It is pinned to
 *     a fixed amber instead.
 *   * Greys, backgrounds, shadows and radii. Making the surface configurable multiplies the
 *     ways a clinic can render its own app unreadable, for very little gain.
 *   * Chart series colours, which are not in this file at all -- see chartPalette.ts.
 */
/*
 * MUI's default breakpoints, written out because `typography` is being built here as the
 * argument to `createTheme` and so cannot yet reach `theme.breakpoints`. This theme does
 * not override `breakpoints`, so these are the same values the `sx` prop resolves `sm` and
 * `md` to -- if that ever changes, these move with it.
 */
const UP_SM = '@media (min-width:600px)';
const UP_MD = '@media (min-width:900px)';

/**
 * The floor for anything a finger has to hit.
 *
 * Applied through `coarsePointerOnly`, so it costs a mouse-driven screen nothing. Measured
 * across the app, every control was *almost* there and none of them arrived: buttons 43px,
 * icon buttons and the language picker 40, toggle buttons 39, checkboxes 38. Near-misses
 * are the bad case -- big enough to look deliberate, small enough that a thumb lands
 * between two of them.
 *
 * A minimum, never a fixed size, so anything already larger is left exactly as it is.
 */
const TOUCH_TARGET = { minHeight: 44, minWidth: 44 } as const;

export const createAppTheme = (
  primaryColor?: string | null,
  secondaryColor?: string | null,
): Theme => {
  const tokens: BrandTokens = deriveBrandTokens(primaryColor, secondaryColor);
  const { primary, secondary } = tokens;

  return createTheme({
    palette: {
      primary: {
        main: primary.main,
        dark: primary.dark,
        light: primary.light,
        // Computed, never assumed white: a clinic may pick a pale primary.
        contrastText: primary.contrastText,
      },
      secondary: {
        main: secondary.main,
        dark: secondary.dark,
        light: secondary.light,
        contrastText: secondary.contrastText,
      },
      background: {
        default: '#F7F8FA',
        paper: '#FFFFFF',
      },
      text: {
        primary: brandColors.foreground,
        secondary: '#5B6172',
      },
      grey: {
        50: '#F8F9FA',
        100: '#F1F3F4',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#6B7280',
        600: '#4B5563',
        700: '#374151',
        800: '#1F2937',
        900: '#111827',
      },
      success: {
        main: '#10B981',
        light: '#34D399',
        dark: '#059669',
      },
      error: {
        main: '#EF4444',
        light: '#F87171',
        dark: '#DC2626',
      },
      // Fixed amber. See the note above on why this no longer follows the secondary.
      warning: {
        main: '#F0B429',
        light: '#F7D070',
        dark: '#DE911D',
      },
    },
    typography: {
      fontFamily: '"Hanken Grotesk", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      /*
       * The display sizes scale with the screen; h4 down does not, and the split is
       * deliberate.
       *
       * h1-h3 are the marketing scale -- 40px of headline is right on a laptop and takes
       * four lines of a 320px phone. Written mobile-first, so the base is the phone size
       * and the breakpoints grow it back to what the desktop had before.
       *
       * Most h1/h2 call sites already set their own responsive `fontSize` and are
       * unaffected by this; the ones that do not -- About's heading, Our Services' -- were
       * the reason a flat scale was worth fixing.
       *
       * h4-h6 stay flat because they are already phone-sized (24px down to 18px) and they
       * are what page and section titles use, 79 of them. Shrinking those is a design
       * change to every screen in the app, not a fix for anything measured.
       */
      h1: {
        fontSize: '1.875rem',
        [UP_SM]: { fontSize: '2.125rem' },
        [UP_MD]: { fontSize: '2.5rem' },
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '1.625rem',
        [UP_SM]: { fontSize: '1.75rem' },
        [UP_MD]: { fontSize: '2rem' },
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontSize: '1.375rem',
        [UP_SM]: { fontSize: '1.5rem' },
        [UP_MD]: { fontSize: '1.75rem' },
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            scrollBehavior: 'smooth',
          },
          body: {
            backgroundColor: '#F7F8FA',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 999, // pill
            padding: '10px 24px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
            // The single most-felt hover in the app -- it lands on every button on
            // every screen, and on a phone it left the last button tapped sitting
            // 1px high under a 20px shadow for as long as the page stayed open.
            ...hoverOnly({
              boxShadow: '0 8px 20px rgba(16,24,40,0.14)',
              transform: 'translateY(-1px)',
            }),
            ...coarsePointerOnly(TOUCH_TARGET),
          },
          sizeLarge: {
            padding: '14px 32px',
            fontSize: '1.05rem',
          },
          containedPrimary: {
            backgroundColor: primary.main,
            color: primary.contrastText,
            ...hoverOnly({ backgroundColor: primary.dark }),
          },
          containedSecondary: {
            backgroundColor: secondary.main,
            color: secondary.contrastText,
            ...hoverOnly({ backgroundColor: secondary.dark }),
          },
          outlinedPrimary: {
            borderColor: primary.main,
            color: primary.main,
            ...hoverOnly({
              borderColor: primary.dark,
              backgroundColor: `rgba(${primary.rgb}, 0.06)`,
            }),
          },
        },
      },
      MuiTextField: {
        /*
         * A wheel event over a *focused* `<input type="number">` is an increment in every
         * browser, so scrolling a long form past a field somebody has clicked into silently
         * edits the number they just typed -- the Our Services editor, three of them per
         * price row, is where this was noticed.
         *
         * Blurring is the fix rather than `preventDefault()`. The increment is the event's
         * default action, computed after dispatch, so an input blurred during dispatch takes
         * none of it while the scroll still reaches the page. `preventDefault()` would stop
         * the increment too, but by swallowing the whole event -- leaving the page stuck
         * under the cursor, which is the same complaint from the other side.
         *
         * Set here rather than at the eleven `type="number"` call sites because the flaw is
         * in the control's own behaviour, not in anything the app asked for -- the same
         * reason the tap-target floor below lives in the theme. `onWheel` is not a TextField
         * prop, so it lands on the root element; wheel events bubble there from the input,
         * which is why `event.target` rather than `currentTarget` is the one to test.
         */
        defaultProps: {
          onWheel: (event: WheelEvent<HTMLDivElement>): void => {
            const { target } = event;
            if (target instanceof HTMLInputElement
              && target.type === 'number'
              && target === document.activeElement) {
              target.blur();
            }
          },
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              // Written out rather than via hoverOnly: the guarded selector reaches a
              // descendant, not the hovered element itself. Guarded all the same, so
              // that "every :hover sits behind (hover: hover)" stays a rule with no
              // exceptions to remember.
              '@media (hover: hover)': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: primary.main,
                },
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: primary.main,
                borderWidth: 2,
              },
            },
          },
        },
      },
      /*
       * The rest of the tap-target floor -- see `TOUCH_TARGET`.
       *
       * Set here rather than at each call site because the shortfall is in the components'
       * own defaults, not in anything the app asked for: every Day/Week switch, every
       * hamburger, every checkbox in the tree inherits the same near-miss.
       *
       * `MuiSvgIcon` is deliberately absent. An icon inside a button is not the target --
       * the button is -- and growing the glyph would make the app look zoomed rather than
       * easier to hit.
       */
      MuiIconButton: {
        styleOverrides: {
          root: coarsePointerOnly(TOUCH_TARGET),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: coarsePointerOnly(TOUCH_TARGET),
        },
      },
      // The base of Checkbox, Radio and Switch, so one override covers all three.
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.MuiCheckbox-root, &.MuiRadio-root': coarsePointerOnly(TOUCH_TARGET),
          },
        },
      },
      /*
       * Reaches the language picker, which is a `Select` and so barely 40px tall.
       *
       * Safe to apply to every input: a text field is already ~56px, and this is a minimum,
       * so the only control it actually moves is the one that was short.
       */
      MuiInputBase: {
        styleOverrides: {
          root: coarsePointerOnly({ minHeight: 44 }),
        },
      },
      /*
       * A Select needs the inner element sized too, not just its wrapper.
       *
       * `MuiSelect-select` is the div that carries `role="combobox"` and takes the tap;
       * the `InputBase` root around it is only a frame. Sizing the frame alone left the
       * language picker with a 44px box and a 40px target inside it.
       */
      MuiSelect: {
        styleOverrides: {
          select: coarsePointerOnly({
            minHeight: 44,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
          }),
        },
      },
      /*
       * Let a long option wrap instead of being cut off.
       *
       * MenuItem is `white-space: nowrap` by default, and the menu paper is capped to the
       * width of the field that opened it, so on a phone the booking form's service names
       * were sliced mid-word -- "... (membru inferi" -- with the price and duration, the
       * two things the choice turns on, simply absent. There is no horizontal scroll in a
       * menu, so nothing recovers them.
       *
       * Set on the theme rather than on the booking form: every Select in the app narrows
       * on a phone, so every one of them has the same problem.
       *
       * Only the open menu wraps. A closed Select renders its value into `.MuiSelect-select`,
       * which keeps its own nowrap-and-ellipsis and so stays one line tall.
       */
      MuiMenuItem: {
        styleOverrides: {
          root: {
            whiteSpace: 'normal',
            // For a long unbroken token -- an email address, say -- that still will not fit
            // once wrapped. Plain prose is unaffected.
            overflowWrap: 'break-word',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            boxShadow: softShadow,
            border: '1px solid rgba(16,24,40,0.06)',
            transition: 'transform .3s ease, box-shadow .3s ease, border-color .3s ease',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 16,
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'inherit',
        },
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            color: brandColors.foreground,
            borderBottom: '1px solid rgba(16,24,40,0.07)',
            boxShadow: '0 1px 12px rgba(16,24,40,0.05)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
    },
  });
};

/**
 * The platform's theme: Carefull's own colours.
 *
 * Used before a clinic is known, and as the base every test renders against.
 */
export const defaultAppTheme: Theme = createAppTheme();

export { softShadow, softShadowHover };
export default defaultAppTheme;
